import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// P17-UAT-15 REV-D1A.2 · SO-style Weaving Consumption transaction popup.
// WO-first source → verify Production / QA weight / BOM / stock → READY TO RUN →
// RUN CONSUMPTION (confirm) → POSTED. Backend engine sealed (reuse only).

const fmt = (v, d = 3) => (v === null || v === undefined || v === "" ? "—" : Number(v).toFixed(d));
const sect = "border border-[var(--border)] rounded-sm bg-[var(--bg-0)] p-3";
const h = "text-[10px] uppercase tracking-widest text-zinc-500 mb-2";
const TOL = { WITHIN: "text-emerald-300", OUT: "text-amber-300" };
const RUN_ST = {
  DRAFT: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  POSTED: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  REVERSED: "bg-red-500/15 text-red-300 border-red-500/40",
};

export function WeavingConsumptionDialog({ open, runId, mode, canPost, onClose, onChanged }) {
  const [wos, setWos] = useState([]);
  const [selectedWos, setSelectedWos] = useState([]); // explicit WO objects (REV-A)
  const [product, setProduct] = useState(null);        // {product_code, description, bom_version}
  const [run, setRun] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [peOpen, setPeOpen] = useState([]);      // expanded WO nos in Production Entry evidence
  const togglePe = (w) => setPeOpen((p) => p.includes(w) ? p.filter((x) => x !== w) : [...p, w]);

  const loadRun = useCallback(async (rid) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/weaving-consumption/runs/${rid}`);
      setRun(data.run);
      const pv = await api.get(`/weaving-consumption/runs/${rid}/preview`);
      setPreview(pv.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load run"); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    setRun(null); setPreview(null); setSelectedWos([]); setProduct(null);
    setConfirm(false); setPeOpen([]); setPickerOpen(false);
    if (runId) { loadRun(runId); return; }
    api.get("/weaving-consumption/eligible-work-orders")
      .then(({ data }) => setWos(data.work_orders || [])).catch(() => setWos([]));
  }, [open, runId, loadRun]);

  // REV-A · NON-PERSISTING preview whenever the explicit WO selection changes.
  useEffect(() => {
    if (run || !product || selectedWos.length === 0) { if (!run) setPreview(null); return; }
    let cancelled = false;
    setBusy(true);
    api.post("/weaving-consumption/preview", {
      product_code: product.product_code, wo_ids: selectedWos.map((w) => w.wo_id),
    }).then(({ data }) => { if (!cancelled) setPreview(data); })
      .catch((e) => { if (!cancelled) { setPreview(null); toast.error(e?.response?.data?.detail || "Preview failed"); } })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [selectedWos, product, run]);

  const selectedIds = selectedWos.map((w) => w.wo_id);
  const pickable = wos.filter((w) => !selectedIds.includes(w.wo_id)
    && (!product || w.product_code === product.product_code));

  const addWO = (w) => {
    if (selectedWos.length === 0) {
      setProduct({ product_code: w.product_code, description: w.product_description, bom_version: w.bom_version });
    } else if (w.product_code !== product.product_code) {
      toast.error("Different Product — one WCR is single-product. Blocked."); return;
    }
    setSelectedWos((p) => [...p, w]);
    setPickerOpen(false);
  };

  const removeWO = (wo_id) => {
    setSelectedWos((p) => {
      const next = p.filter((w) => w.wo_id !== wo_id);
      if (next.length === 0) setProduct(null);
      return next;
    });
  };

  const saveDraft = async () => {
    if (!product || selectedWos.length === 0) { toast.error("Add at least one Work Order"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/weaving-consumption/runs`, {
        product_code: product.product_code, wo_ids: selectedWos.map((w) => w.wo_id), shift: "",
      });
      toast.success(`Draft saved · ${data.run.wcr_no}`);
      onChanged && onChanged();
      await loadRun(data.run.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save draft"); }
    finally { setBusy(false); }
  };

  const runConsumption = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/weaving-consumption/runs/${run.id}/run`, {});
      toast.success(data.idempotent ? "Already posted" : `Consumption POSTED across ${data.posted_wos} WO(s)`);
      setConfirm(false);
      onChanged && onChanged();
      await loadRun(run.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); setConfirm(false); }
    finally { setBusy(false); }
  };

  const posted = run?.status === "POSTED";
  const hdr = preview?.header;
  const ready = !!preview?.ready;
  const viewMode = mode === "view";
  const canRun = !viewMode && !!run && preview?.can_run && canPost && !posted;
  const includedEntries = preview?.included_entries || [];
  const peByWo = includedEntries.reduce((m, e) => { const k = e.wo_no || e.wo_id; (m[k] = m[k] || []).push(e); return m; }, {});
  const allLots = (preview?.wo_allocation || []).flatMap((w) => w.lots || []);
  const resolvedKg = allLots.reduce((s, l) => s + (Number(l.qty) || 0), 0);
  const legCount = allLots.length;
  const woList = (preview?.wo_allocation || []).map((w) => w.wo_no || w.wo_id);
  const prodDates = (preview?.daily_reconciliation || []).map((d) => d.production_date);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-5xl max-h-[92vh] overflow-y-auto"
        data-testid="wc-dialog" description="Weaving Consumption transaction — verify evidence, then run consumption.">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            {run ? `Weaving Consumption · ${run.wcr_no}` : "New Weaving Consumption Run · (unsaved)"}
            {run && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border uppercase ${RUN_ST[run.status] || ""}`} data-testid="wc-dialog-status">{run.status}</span>}
            {!run && <span className="text-[10px] font-bold px-2 py-0.5 rounded-sm border uppercase bg-zinc-500/15 text-zinc-300 border-zinc-500/40" data-testid="wc-dialog-status">UNSAVED</span>}
          </DialogTitle>
        </DialogHeader>

        {/* SECTION A · Source — WO-first, explicit inclusion (create mode) */}
        {!run && (
          <div className={sect} data-testid="wc-section-source">
            <div className="flex items-center justify-between mb-2">
              <div className={h}>A · Work Orders · explicit selection</div>
              <button data-testid="wc-add-wo" onClick={() => setPickerOpen(true)}
                className="h-8 px-3 rounded-sm bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold">
                {selectedWos.length === 0 ? "Select Primary Work Order" : "+ Add Work Order"}
              </button>
            </div>
            {product && <div className="mb-2 text-sm text-zinc-300">Product: <span className="font-mono text-white">{product.product_code}</span> · {product.description} · BOM v{product.bom_version || "—"}</div>}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-[10px] uppercase text-zinc-500 border-b border-[var(--border)]">
                  <th className="px-2 py-1.5 text-left">WO</th><th className="px-2 py-1.5 text-left">SO/ESO</th>
                  <th className="px-2 py-1.5 text-right">Produced</th><th className="px-2 py-1.5 text-right">Prev. Consumed</th>
                  <th className="px-2 py-1.5 text-right">Available</th><th className="px-2 py-1.5 text-right">Qty Included</th>
                  <th className="px-2 py-1.5 text-center">Action</th>
                </tr></thead>
                <tbody data-testid="wc-selected-wos">
                  {selectedWos.map((w) => (
                    <tr key={w.wo_id} className="border-b border-[var(--border)]/30" data-testid={`wc-selwo-${w.wo_no}`}>
                      <td className="px-2 py-1.5 font-mono text-zinc-200">{w.wo_no}</td>
                      <td className="px-2 py-1.5 font-mono text-zinc-500">{w.so_eso || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmt(w.produced, 2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-zinc-400">{fmt(w.consumed, 2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(w.available, 2)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(w.available, 2)}</td>
                      <td className="px-2 py-1.5 text-center">
                        <button data-testid={`wc-remove-wo-${w.wo_no}`} onClick={() => removeWO(w.wo_id)}
                          className="h-6 px-2 rounded-sm border border-red-500/40 text-red-300 text-[10px] hover:bg-red-500/10">Remove</button>
                      </td>
                    </tr>
                  ))}
                  {selectedWos.length === 0 && <tr><td colSpan={7} className="px-2 py-4 text-center text-zinc-600">No Work Orders added — use “Select Primary Work Order”.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Verification sections (non-persisting preview OR persisted run) */}
        {preview && (
          <div className="space-y-3">
            {/* F · summary card (top for quick read) */}
            {hdr && (
              <div className={sect} data-testid="wc-summary">
                <div className={h}>Summary</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Product</span><span className="font-mono text-white">{hdr.product_code}</span></div>
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Date Range</span><span className="font-mono text-zinc-300">{hdr.scope_from} → {hdr.scope_to}</span></div>
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Total Metres</span><span className="font-mono text-white">{fmt(hdr.total_metres, 2)}</span></div>
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Dates</span><span className="font-mono text-zinc-300">{hdr.date_count}</span></div>
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Expected Input</span><span className="font-mono text-emerald-300" data-testid="wc-total-mass">{fmt(hdr.total_expected_mass_kg, 3)} kg</span></div>
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Weighted Running</span><span className="font-mono text-zinc-300" data-testid="wc-weighted">{fmt(hdr.weighted_running_gpm, 3)} g/m</span></div>
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Readiness</span><span className={`font-mono font-bold ${ready || posted ? "text-emerald-300" : "text-amber-300"}`} data-testid="wc-readiness">{posted ? "POSTED" : (ready ? "READY TO RUN" : "BLOCKED")}</span></div>
                </div>
              </div>
            )}

            {/* A · Production Entry Evidence — WO-wise, expandable */}
            {includedEntries.length > 0 && (
              <div className={sect} data-testid="wc-section-production-evidence">
                <div className={h}>A · Production Entry Evidence · WO-wise (expandable)</div>
                {Object.entries(peByWo).map(([woNo, rows]) => {
                  const open = peOpen.includes(woNo);
                  const incl = rows.reduce((s, r) => s + (Number(r.eligible_metres) || 0), 0);
                  const soeso = (rows[0] && rows[0].so_eso) || "—";
                  return (
                    <div key={woNo} className="mb-2 border border-[var(--border)]/50 rounded-sm">
                      <button type="button" data-testid={`wc-pe-toggle-${woNo}`} onClick={() => togglePe(woNo)}
                        className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-white/5">
                        <span className="font-mono text-zinc-200">{woNo} · SO/ESO {soeso} · {hdr?.product_code}</span>
                        <span className="text-zinc-400">{rows.length} entry(s) · Included {fmt(incl, 2)} m <span className="text-zinc-500">{open ? "▾" : "▸"}</span></span>
                      </button>
                      {open && (
                        <table className="w-full text-[11px] border-t border-[var(--border)]/40" data-testid={`wc-pe-table-${woNo}`}>
                          <thead><tr className="text-[9px] uppercase text-zinc-500">
                            <th className="px-2 py-1 text-left">Entry No</th><th className="px-2 py-1 text-left">Prod. Date</th>
                            <th className="px-2 py-1 text-left">Machine</th><th className="px-2 py-1 text-left">Shift</th>
                            <th className="px-2 py-1 text-right">Produced</th><th className="px-2 py-1 text-right">Prev. Consumed</th>
                            <th className="px-2 py-1 text-right">Remaining</th><th className="px-2 py-1 text-right">Included</th>
                          </tr></thead>
                          <tbody>
                            {rows.map((r, i) => {
                              const rem = (Number(r.produced_meter) || 0) - (Number(r.consumed_metres) || 0);
                              return (
                                <tr key={i} className="border-t border-[var(--border)]/20" data-testid={`wc-pe-row-${r.entry_no || i}`}>
                                  <td className="px-2 py-1 font-mono text-zinc-300">{r.entry_no || "—"}</td>
                                  <td className="px-2 py-1 font-mono text-zinc-400">{r.entry_date}</td>
                                  <td className="px-2 py-1 font-mono text-zinc-400">{r.machine_id || "—"}</td>
                                  <td className="px-2 py-1 font-mono text-zinc-400">{r.shift || "—"}</td>
                                  <td className="px-2 py-1 text-right font-mono">{fmt(r.produced_meter, 2)}</td>
                                  <td className="px-2 py-1 text-right font-mono text-zinc-400">{fmt(r.consumed_metres, 2)}</td>
                                  <td className="px-2 py-1 text-right font-mono text-amber-200">{fmt(rem, 2)}</td>
                                  <td className="px-2 py-1 text-right font-mono text-emerald-300">{fmt(r.eligible_metres, 2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* B · QA daily reconciliation */}
            <div className={sect} data-testid="wc-section-qa">
              <div className={h}>B · QA Running Weight ↔ Daily Reconciliation (read-only)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-[10px] uppercase text-zinc-500 border-b border-[var(--border)]">
                    <th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-right">Metres</th>
                    <th className="px-2 py-1.5 text-right">Actual g/m</th><th className="px-2 py-1.5 text-right">Std</th>
                    <th className="px-2 py-1.5 text-right">Var %</th><th className="px-2 py-1.5 text-center">Tol</th>
                    <th className="px-2 py-1.5 text-right">Expected kg</th><th className="px-2 py-1.5 text-left">Authorised By</th>
                    <th className="px-2 py-1.5 text-center">QA</th>
                  </tr></thead>
                  <tbody>
                    {(preview.daily_reconciliation || []).map((d) => (
                      <tr key={d.production_date} className="border-b border-[var(--border)]/30">
                        <td className="px-2 py-1.5 font-mono text-zinc-300">{d.production_date}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmt(d.produced_metres, 2)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-white">{fmt(d.daily_average_gpm, 3)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-zinc-400">{fmt(d.standard_gpm, 3)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmt(d.variance_pct, 2)}</td>
                        <td className={`px-2 py-1.5 text-center font-mono ${TOL[d.tolerance_result] || "text-zinc-500"}`}>{d.tolerance_result || "—"}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(d.expected_mass_kg, 3)}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-400">{d.authorized_by || "—"}</td>
                        <td className="px-2 py-1.5 text-center">{d.qa_authorised
                          ? <span className="text-[9px] font-bold px-1 rounded-sm border uppercase bg-emerald-500/15 text-emerald-300 border-emerald-400/40">AUTH</span>
                          : <span className="text-[9px] font-bold px-1 rounded-sm border uppercase bg-red-500/15 text-red-300 border-red-500/40" title={d.reason}>NOT AUTH</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* C · BOM components */}
            {preview.bom_components?.length > 0 && (
              <div className={sect} data-testid="wc-section-bom">
                <div className={h}>C · Approved Weaving BOM</div>
                <table className="w-full text-xs">
                  <thead><tr className="text-[10px] uppercase text-zinc-500 border-b border-[var(--border)]">
                    <th className="px-2 py-1.5 text-left">Component</th><th className="px-2 py-1.5 text-left">Item</th>
                    <th className="px-2 py-1.5 text-right">BOM g/m</th><th className="px-2 py-1.5 text-right">Ratio %</th>
                    {posted && <th className="px-2 py-1.5 text-right">Posted kg</th>}
                  </tr></thead>
                  <tbody>
                    {preview.bom_components.map((c, i) => (
                      <tr key={i} className="border-b border-[var(--border)]/30" data-testid={`wc-bom-row-${i}`}>
                        <td className="px-2 py-1.5 text-zinc-300">{c.klass || c.routing}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-400">{c.rm_code || c.item_code || "—"}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{fmt(c.bom_gpm ?? c.gms_per_metre, 3)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-white">{fmt(c.ratio_pct, 2)}%</td>
                        {posted && <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(c.posted_kg, 4)}</td>}
                      </tr>
                    ))}
                    <tr className="font-semibold"><td className="px-2 py-1.5 text-zinc-400" colSpan={3}>Ratio Total</td><td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(preview.bom_components.reduce((s, c) => s + (c.ratio_pct || 0), 0), 2)}%</td>{posted && <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(preview.bom_components.reduce((s, c) => s + (c.posted_kg || 0), 0), 4)}</td>}</tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* D+E · WO allocation with expandable stock/lot */}
            <div className={sect} data-testid="wc-section-alloc">
              <div className={h}>{posted ? "D · WO-wise Frozen Posting & E · Actual Physical Consumption (historical evidence)" : "D · WO-wise Allocation & E · Physical Stock / Lot"}</div>
              {(preview.wo_allocation || []).map((w) => (
                <div key={w.wo_id} className="mb-2 border border-[var(--border)]/50 rounded-sm">
                  <div className="flex items-center justify-between px-3 py-2 text-xs">
                    <span className="font-mono text-zinc-200">{w.wo_no || w.wo_id}</span>
                    <span className="text-zinc-400">Metres {fmt(w.metres, 2)} · Std {fmt(w.standard_target_kg, 3)} kg · Running {fmt(w.running_expected_kg, 3)} kg · {posted ? <span className="text-emerald-300">POSTED / CONSUMED</span> : (w.resolvable ? <span className="text-emerald-300">AVAILABLE</span> : <span className="text-red-400">INSUFFICIENT / INELIGIBLE</span>)}</span>
                  </div>
                  {(w.lots || []).length > 0 && (
                    <table className="w-full text-[11px] border-t border-[var(--border)]/40">
                      <thead><tr className="text-[9px] uppercase text-zinc-500">
                        <th className="px-2 py-1 text-left">Date</th><th className="px-2 py-1 text-left">Component</th>
                        <th className="px-2 py-1 text-left">{posted ? "Material" : "Item"}</th><th className="px-2 py-1 text-left">Godown</th>
                        <th className="px-2 py-1 text-left">Lot/Batch</th>
                        {posted
                          ? (<><th className="px-2 py-1 text-right">Required kg</th><th className="px-2 py-1 text-right">Posted kg</th><th className="px-2 py-1 text-right">Post Balance</th><th className="px-2 py-1 text-center">Status</th></>)
                          : (<><th className="px-2 py-1 text-right">Avail</th><th className="px-2 py-1 text-right">Post Qty</th><th className="px-2 py-1 text-center">Status</th></>)}
                      </tr></thead>
                      <tbody>
                        {w.lots.map((l, i) => (
                          <tr key={i} className="border-t border-[var(--border)]/20" data-testid={`wc-lot-row-${i}`}>
                            <td className="px-2 py-1 font-mono text-zinc-500">{l.entry_date}</td>
                            <td className="px-2 py-1 text-zinc-300">{l.component}</td>
                            <td className="px-2 py-1 font-mono text-zinc-400">{l.item_code}</td>
                            <td className="px-2 py-1 font-mono text-zinc-400" data-testid="wc-lot-godown" title={l.godown_id}>{l.godown_name || "Unknown Godown"}{l.godown_code ? ` (${l.godown_code})` : ""}</td>
                            <td className="px-2 py-1 font-mono text-zinc-400">{l.batch_lot}</td>
                            {posted ? (
                              <>
                                <td className="px-2 py-1 text-right font-mono text-zinc-400">{fmt(l.required_kg, 4)}</td>
                                <td className="px-2 py-1 text-right font-mono text-emerald-300">{fmt(l.qty, 4)}</td>
                                <td className="px-2 py-1 text-right font-mono text-zinc-300">{l.post_balance === null || l.post_balance === undefined ? "—" : fmt(l.post_balance, 4)}</td>
                                <td className="px-2 py-1 text-center text-emerald-300">CONSUMED</td>
                              </>
                            ) : (
                              <>
                                <td className="px-2 py-1 text-right font-mono">{fmt(l.available_qty, 2)}</td>
                                <td className="px-2 py-1 text-right font-mono text-emerald-300">{fmt(l.qty, 3)}</td>
                                <td className="px-2 py-1 text-center text-emerald-300">{l.status}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>

            {!posted && preview.reasons?.length > 0 && (
              <div className="text-xs text-red-300" data-testid="wc-reasons">
                {preview.reasons.map((x, i) => <div key={i}>• {x}</div>)}
              </div>
            )}

            {posted && (
              <div className={sect} data-testid="wc-posted-info">
                <div className={h}>Posted · Read-only (Save / Verify / Run Consumption no longer available)</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Posted By</span><span className="font-mono text-zinc-300">{run.posted_by || "—"}</span></div>
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Posted At</span><span className="font-mono text-zinc-400">{run.posted_at || "—"}</span></div>
                  <div><span className="text-zinc-500 text-[10px] uppercase block">Total Posted</span><span className="font-mono text-emerald-300">{fmt(run.reporting?.total_expected_mass_kg, 3)} kg</span></div>
                  <div><span className="text-zinc-500 text-[10px] uppercase block">V2 Groups</span><span className="font-mono text-zinc-400">{(run.conversion_group_ids || []).length}</span></div>
                </div>
                {(run.conversion_group_ids || []).length > 0 && (
                  <div className="mt-3">
                    <span className="text-zinc-500 text-[10px] uppercase block mb-1">V2 Transaction / Group References</span>
                    <div className="flex flex-wrap gap-1" data-testid="wc-posted-groups">
                      {(run.conversion_group_ids || []).map((g, i) => (
                        <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-emerald-400/30 bg-emerald-500/10 text-emerald-200">{g}</span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-xs text-zinc-500 mt-2">This transaction is POSTED and locked. QA weight, BOM calculation and physical-lot evidence above are retained as historical evidence.</p>
              </div>
            )}
          </div>
        )}

        {/* Action bar */}
        <div className="flex flex-wrap gap-2 justify-end border-t border-[var(--border)] pt-3">
          {!run && canPost && (
            <button data-testid="wc-save-draft" onClick={saveDraft} disabled={busy || selectedWos.length === 0}
              className="h-9 px-5 rounded-sm bg-sky-600 hover:bg-sky-500 text-white text-sm font-bold disabled:opacity-40">Save Draft</button>
          )}
          {canRun && (
            <button data-testid="wc-run-consumption" onClick={() => setConfirm(true)} disabled={busy}
              className="h-9 px-5 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-40">RUN CONSUMPTION</button>
          )}
          <button data-testid="wc-dialog-close" onClick={onClose} className="h-9 px-4 rounded-sm border border-[var(--border)] text-zinc-400 text-sm">Close</button>
        </div>
      </DialogContent>

      {/* + Add Work Order · eligible picker (create mode) */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="wc-wo-picker" description="Select an eligible Work Order to include.">
          <DialogHeader><DialogTitle className="font-display">
            {selectedWos.length === 0 ? "Select Primary Work Order" : `Add Work Order · ${product?.product_code || ""}`}
          </DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500 mb-2">{selectedWos.length === 0
            ? "Choose the first Work Order — its Product and applicable BOM define the transaction."
            : "Only Work Orders with the same Product and applicable approved Weaving BOM are offered."}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-[10px] uppercase text-zinc-500 border-b border-[var(--border)]">
                <th className="px-2 py-1.5 text-left">WO</th><th className="px-2 py-1.5 text-left">Product</th>
                <th className="px-2 py-1.5 text-left">SO/ESO</th><th className="px-2 py-1.5 text-right">Produced</th>
                <th className="px-2 py-1.5 text-right">Available</th><th className="px-2 py-1.5 text-center">Add</th>
              </tr></thead>
              <tbody>
                {pickable.map((w) => (
                  <tr key={w.wo_id} className="border-b border-[var(--border)]/30 hover:bg-white/5" data-testid={`wc-pick-${w.wo_no}`}>
                    <td className="px-2 py-1.5 font-mono text-zinc-200">{w.wo_no}</td>
                    <td className="px-2 py-1.5 font-mono text-zinc-400">{w.product_code}</td>
                    <td className="px-2 py-1.5 font-mono text-zinc-500">{w.so_eso || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmt(w.produced, 2)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{fmt(w.available, 2)}</td>
                    <td className="px-2 py-1.5 text-center">
                      <button data-testid={`wc-pick-add-${w.wo_no}`} onClick={() => addWO(w)}
                        className="h-6 px-3 rounded-sm bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] font-semibold">Add</button>
                    </td>
                  </tr>
                ))}
                {pickable.length === 0 && <tr><td colSpan={6} className="px-2 py-4 text-center text-zinc-600">No eligible Work Orders available.</td></tr>}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Run confirmation */}
      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-md" data-testid="wc-confirm-dialog" description="Confirm posting weaving consumption to V2 stock.">
          <DialogHeader><DialogTitle className="font-display">Run Weaving Consumption</DialogTitle></DialogHeader>
          {hdr && (
            <div className="text-sm space-y-2" data-testid="wc-confirm-summary">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                <div><span className="text-zinc-500 text-[10px] uppercase block">Consumption Run No.</span><span className="font-mono text-white">{run?.wcr_no}</span></div>
                <div><span className="text-zinc-500 text-[10px] uppercase block">Product</span><span className="font-mono text-white">{hdr.product_code}</span></div>
                <div><span className="text-zinc-500 text-[10px] uppercase block">Included Metres</span><span className="font-mono text-white">{fmt(hdr.total_metres, 2)} m</span></div>
                <div><span className="text-zinc-500 text-[10px] uppercase block">Production Date(s)</span><span className="font-mono text-zinc-300">{prodDates.join(", ") || "—"}</span></div>
                <div className="col-span-2"><span className="text-zinc-500 text-[10px] uppercase block">Work Order(s)</span><span className="font-mono text-zinc-300">{woList.join(", ") || "—"}</span></div>
                <div><span className="text-zinc-500 text-[10px] uppercase block">Expected Consumption</span><span className="font-mono text-emerald-300">{fmt(hdr.total_expected_mass_kg, 3)} kg</span></div>
                <div><span className="text-zinc-500 text-[10px] uppercase block">Resolved Consumption</span><span className="font-mono text-emerald-300" data-testid="wc-confirm-resolved">{fmt(resolvedKg, 3)} kg</span></div>
                <div className="col-span-2"><span className="text-zinc-500 text-[10px] uppercase block">V2 Posting Summary</span><span className="font-mono text-zinc-300" data-testid="wc-confirm-legs">{legCount} physical lot leg(s) across {woList.length} WO(s)</span></div>
              </div>
              <p className="text-xs text-amber-300 mt-1">This action posts physical weaving INPUT consumption to V2 stock against the listed Work Orders and physical lots. It cannot be edited after posting.</p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button data-testid="wc-confirm-cancel" onClick={() => setConfirm(false)} className="h-9 px-4 rounded-sm border border-[var(--border)] text-zinc-400 text-sm">Cancel</button>
            <button data-testid="wc-confirm-run" onClick={runConsumption} disabled={busy}
              className="h-9 px-4 rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold disabled:opacity-40">Confirm Run Consumption</button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default WeavingConsumptionDialog;
